import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

import stetic1 from "../../static/media/CorporalStetic/stetic1.webp";
import stetic2 from "../../static/media/CorporalStetic/stetic2.webp";
import stetic3 from "../../static/media/CorporalStetic/stetic3.webp";
import stetic4 from "../../static/media/CorporalStetic/stetic4.webp";
import stetic5 from "../../static/media/CorporalStetic/stetic5.webp";
import stetic6 from "../../static/media/CorporalStetic/stetic6.webp";
import stetic7 from "../../static/media/CorporalStetic/stetic7.webp";
import stetic8 from "../../static/media/CorporalStetic/stetic8.webp";

const PRIMARY = "#06BBC7";

const services = [
  {
    title: "Reducción de medidas",
    description: "Disminuimos tallas y volúmenes para realzar tu figura.",
    image: stetic1,
  },
  {
    title: "Moldeamiento corporal",
    description: "Definimos y armonizamos tu silueta.",
    image: stetic2,
  },
  {
    title: "Reafirmación",
    description: "Mejoramos la firmeza y elasticidad de tu piel.",
    image: stetic3,
  },
  {
    title: "Aparatología",
    description: "Tecnología estética para resultados seguros.",
    image: stetic4,
  },
  {
    title: "Metaloterapia",
    description: "Estimulación corporal con técnicas especializadas.",
    image: stetic5,
  },
  {
    title: "Depilación",
    description: "Piel suave con técnicas efectivas y cuidadas.",
    image: stetic6,
  },
  {
    title: "Carboxiterapia",
    description: "Apoyo estético para circulación y adiposidad.",
    image: stetic7,
  },
  {
    title: "Desintoxicación iónica",
    description: "Equilibrio y bienestar mediante terapia iónica.",
    image: stetic8,
  },
];

const CardMotion = motion(Box);

const CorporalEstetic = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const cardsRef = useRef(null);

  const whatsappMessage = encodeURIComponent(
    "Hola Lily's Sunflower, quiero agendar una cita y recibir más información sobre estética corporal."
  );

  const whatsappUrl = `https://wa.me/593984781610?text=${whatsappMessage}`;
  const mobilePages = Math.ceil(services.length / 2);

  const visibleServices = useMemo(() => {
    if (!isMobile) return services;
    return services.slice(page * 2, page * 2 + 2);
  }, [isMobile, page]);

  const scrollToCards = () => {
    setTimeout(() => {
      cardsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  };

  const handlePrev = () => {
    setPage((prev) => (prev === 0 ? mobilePages - 1 : prev - 1));
    scrollToCards();
  };

  const handleNext = () => {
    setPage((prev) => (prev === mobilePages - 1 ? 0 : prev + 1));
    scrollToCards();
  };

  const handleDotClick = (index) => {
    setPage(index);
    scrollToCards();
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 9 },
        background:
          "linear-gradient(180deg, #ffffff 0%, #ffffff 72%, rgba(6, 187, 199, 0.08) 100%)",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              width: { xs: 190, sm: 280 },
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              color: PRIMARY,
            }}
          >
            <Box sx={{ flex: 1, height: 2, bgcolor: PRIMARY, borderRadius: 10 }} />
            <Box sx={{ width: 7, height: 7, bgcolor: PRIMARY, borderRadius: "50%" }} />
            <Typography
              component="span"
              sx={{
                fontSize: { xs: 26, sm: 32 },
                lineHeight: 1,
                color: PRIMARY,
                fontWeight: 300,
              }}
            >
              ✽
            </Typography>
            <Box sx={{ width: 7, height: 7, bgcolor: PRIMARY, borderRadius: "50%" }} />
            <Box sx={{ flex: 1, height: 2, bgcolor: PRIMARY, borderRadius: 10 }} />
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: "2.15rem", sm: "2.8rem", md: "3.5rem" },
              fontWeight: 500,
              color: "#1d2530",
              letterSpacing: "-0.04em",
              mb: 2,
            }}
          >
            Estética Corporal
          </Typography>

          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              color: "#374151",
              fontSize: { xs: "0.98rem", md: "1.1rem" },
              lineHeight: 1.75,
              fontWeight: 400,
              px: { xs: 1, sm: 0 },
            }}
          >
            Tratamientos pensados para cuidar tu cuerpo, resaltar tu belleza
            natural y ayudarte a sentirte segura en cada etapa de tu proceso.
          </Typography>
        </Box>

        <Box
          ref={cardsRef}
          sx={{
            maxWidth: { xs: 340, sm: 1320 },
            mx: "auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: { xs: 2.6, sm: 1.8, md: 2 },
            scrollMarginTop: { xs: "22px", sm: "40px" },
          }}
        >
          {visibleServices.map((service, index) => (
            <CardMotion
              key={`${service.title}-${isMobile ? page : "desktop"}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 0.38,
                delay: index * 0.035,
                ease: "easeOut",
              }}
              sx={{
                width: "100%",
                bgcolor: "#ffffff",
                borderRadius: { xs: "20px", md: "20px" },
                overflow: "hidden",
                boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
                border: "1px solid rgba(6, 187, 199, 0.13)",
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "43% 57%",
                  lg: "42% 58%",
                },
                minHeight: { xs: "auto", sm: 175, lg: 210 },
                transition: "box-shadow 0.25s ease, border-color 0.25s ease",
                "&:hover": {
                  boxShadow: "0 18px 42px rgba(15, 23, 42, 0.12)",
                  borderColor: "rgba(6, 187, 199, 0.36)",
                },
              }}
            >
              <Box
                sx={{
                  height: { xs: 330, sm: "100%" },
                  bgcolor: "#f8fbfc",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={service.image}
                  alt={service.title}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: { xs: "center top", sm: "center" },
                    display: "block",
                  }}
                />
              </Box>

              <Box
                sx={{
                  p: { xs: 2.1, sm: 1.8, lg: 2 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: { xs: "center", sm: "flex-start" },
                  textAlign: { xs: "center", sm: "left" },
                  minWidth: 0,
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    color: "#1f2937",
                    fontSize: {
                      xs: "1.18rem",
                      sm: "1rem",
                      lg: "1.03rem",
                    },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: { xs: 1, sm: 1 },
                  }}
                >
                  {service.title}
                </Typography>

                <Box
                  sx={{
                    width: { xs: 44, sm: 32 },
                    height: 2,
                    bgcolor: PRIMARY,
                    borderRadius: 10,
                    mb: { xs: 0, sm: 1.2 },
                    mx: { xs: "auto", sm: 0 },
                  }}
                />

                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    color: "#4b5563",
                    fontSize: {
                      sm: "0.85rem",
                      lg: "0.88rem",
                    },
                    lineHeight: 1.55,
                    fontWeight: 400,
                  }}
                >
                  {service.description}
                </Typography>
              </Box>
            </CardMotion>
          ))}
        </Box>

        <Box
          sx={{
            mt: 3.5,
            display: { xs: "flex", sm: "none" },
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <IconButton
            onClick={handlePrev}
            sx={{
              width: 44,
              height: 44,
              color: PRIMARY,
              border: `1px solid ${PRIMARY}`,
              bgcolor: "#ffffff",
              "&:hover": {
                bgcolor: "rgba(6, 187, 199, 0.08)",
              },
            }}
          >
            <KeyboardArrowLeftRoundedIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.9,
            }}
          >
            {Array.from({ length: mobilePages }).map((_, index) => (
              <Box
                key={index}
                component="button"
                type="button"
                onClick={() => handleDotClick(index)}
                aria-label={`Ver grupo ${index + 1}`}
                sx={{
                  width: page === index ? 22 : 8,
                  height: 8,
                  p: 0,
                  border: "none",
                  borderRadius: "999px",
                  cursor: "pointer",
                  bgcolor: page === index ? PRIMARY : "rgba(6, 187, 199, 0.25)",
                  transition: "all 0.25s ease",
                }}
              />
            ))}
          </Box>

          <IconButton
            onClick={handleNext}
            sx={{
              width: 44,
              height: 44,
              color: PRIMARY,
              border: `1px solid ${PRIMARY}`,
              bgcolor: "#ffffff",
              "&:hover": {
                bgcolor: "rgba(6, 187, 199, 0.08)",
              },
            }}
          >
            <KeyboardArrowRightRoundedIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            mt: { xs: 5, md: 6 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2.5,
          }}
        >
          <Typography
            sx={{
              color: "#1f2937",
              fontSize: { xs: "1rem", md: "1.1rem" },
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            Tu mejor versión comienza aquí.
          </Typography>

          <Button
            component="a"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            endIcon={<WhatsAppIcon />}
            sx={{
              bgcolor: PRIMARY,
              color: "#ffffff",
              px: 4,
              py: 1.25,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "1rem",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#05aeb9",
                boxShadow: "none",
              },
            }}
          >
            Agenda tu cita
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CorporalEstetic;